/**
 * Public user auth — backed by Firebase Authentication (Email/Password).
 *
 * Keeps the exact same hook surface as the previous localStorage MVP, so
 * existing call-sites (Header avatar menu, /account, /login, /signup, etc.)
 * keep working unchanged.
 *
 * Storage map:
 *   auth.users           ← Firebase Auth users
 *   users/{uid}          ← public profile (username, displayName, avatar)
 *   users/{uid}.banned   ← boolean flag set by admin to block a user
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export const ADMIN_EMAILS = ["jamal@elhizazi.me", "rawan@elhizazi.me"] as const;

export type PublicUser = {
  id: string;
  email: string;
  username: string;
  displayName: string;
  avatar?: string;
  banned?: boolean;
  emailVerified?: boolean;
  createdAt: number;
};

type Result<T = void> = { ok: true; value?: T } | { ok: false; error: string };

type UserAuthState = {
  user: PublicUser | null;
  loading: boolean;
  isAdmin: boolean;
  needsVerification: boolean;
  pendingEmail: string | null;
  resendVerification: () => Promise<Result>;
  signUp: (input: {
    email: string;
    password: string;
    displayName: string;
    username: string;
  }) => Promise<Result<PublicUser & { needsVerification?: boolean }>>;
  signIn: (email: string, password: string) => Promise<Result<PublicUser>>;
  signOut: () => Promise<void>;
  updateProfile: (
    patch: Partial<Pick<PublicUser, "displayName" | "username" | "avatar">>,
  ) => Promise<Result>;
};

const Ctx = createContext<UserAuthState | null>(null);

/** Translate Firebase error codes to short Arabic messages. */
function fbErr(code: string): string {
  const map: Record<string, string> = {
    "auth/email-already-in-use": "هذا البريد مسجل مسبقاً",
    "auth/invalid-email": "بريد إلكتروني غير صالح",
    "auth/weak-password": "كلمة المرور ضعيفة (6 خانات على الأقل)",
    "auth/user-not-found": "بيانات الدخول غير صحيحة",
    "auth/wrong-password": "بيانات الدخول غير صحيحة",
    "auth/invalid-credential": "بيانات الدخول غير صحيحة",
    "auth/too-many-requests": "محاولات كثيرة، حاول لاحقاً",
    "auth/network-request-failed": "تعذّر الاتصال بالشبكة",
  };
  return map[code] ?? "حدث خطأ، حاول مرة أخرى";
}

async function loadProfile(uid: string, fallback: PublicUser): Promise<PublicUser> {
  const [{ doc, getDoc, serverTimestamp, setDoc }, { getDb }] = await Promise.all([
    import("firebase/firestore"),
    import("./firebase"),
  ]);
  const db = getDb();
  if (!db) return fallback;
  try {
    const snap = await getDoc(doc(db, "users", uid));
    if (!snap.exists()) {
      await setDoc(doc(db, "users", uid), {
        email: fallback.email,
        username: fallback.username,
        displayName: fallback.displayName,
        avatar: fallback.avatar ?? null,
        banned: false,
        createdAt: serverTimestamp(),
      });
      return fallback;
    }
    const d = snap.data();
    const profile: PublicUser = {
      id: uid,
      email: d.email ?? fallback.email,
      username: d.username ?? fallback.username,
      displayName: d.displayName ?? fallback.displayName,
      avatar: d.avatar ?? fallback.avatar,
      banned: !!d.banned,
      createdAt: typeof d.createdAt === "number" ? d.createdAt : fallback.createdAt,
    };
    const { primeUserProfile } = await import("./user-profiles");
    primeUserProfile(uid, {
      displayName: profile.displayName ?? null,
      avatar: profile.avatar ?? null,
    });
    return profile;
  } catch {
    return fallback;
  }
}

function isAdminEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  return (ADMIN_EMAILS as readonly string[]).includes(email.toLowerCase());
}

export function UserAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PublicUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);

  useEffect(() => {
    let unsub: undefined | (() => void);
    let cancelled = false;

    const start = async () => {
      const [{ onAuthStateChanged, signOut: fbSignOut, updateProfile: fbUpdateProfile }, { getFirebaseAuth }] = await Promise.all([
        import("firebase/auth"),
        import("./firebase"),
      ]);
      if (cancelled) return;
      const auth = getFirebaseAuth();
      if (!auth) {
        setLoading(false);
        return;
      }
      unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        setUser(null);
        setLoading(false);
        return;
      }
      // Require email verification for non-admin accounts.
      if (!u.emailVerified && !isAdminEmail(u.email)) {
        setPendingEmail(u.email ?? null);
        setNeedsVerification(true);
        try {
          await fbSignOut(auth);
        } catch {
          /* ignore */
        }
        setUser(null);
        setLoading(false);
        return;
      }
      const base: PublicUser = {
        id: u.uid,
        email: u.email ?? "",
        username: (u.displayName || u.email?.split("@")[0] || "user").trim(),
        displayName: (u.displayName || u.email?.split("@")[0] || "User").trim(),
        avatar: u.photoURL ?? undefined,
        emailVerified: u.emailVerified,
        createdAt: Date.now(),
      };
      const profile = await loadProfile(u.uid, base);
      if (profile.banned) {
        try {
          await fbSignOut(auth);
        } catch {
          /* ignore */
        }
        setUser(null);
      } else {
        try {
          const fsAvatar = profile.avatar ?? null;
          const authAvatar = auth.currentUser?.photoURL ?? null;
          if (auth.currentUser && fsAvatar !== authAvatar) {
            await fbUpdateProfile(auth.currentUser, {
              photoURL: fsAvatar ?? "",
            });
          }
        } catch {
          /* non-fatal */
        }
        setUser({ ...profile, emailVerified: true });
        setNeedsVerification(false);
        setPendingEmail(null);
      }
      setLoading(false);
      });
    };

    const w = window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number; cancelIdleCallback?: (id: number) => void };
    const id = w.requestIdleCallback ? w.requestIdleCallback(() => { void start(); }, { timeout: 5000 }) : window.setTimeout(() => { void start(); }, 2500);
    return () => {
      cancelled = true;
      if (typeof id === "number") {
        if (w.cancelIdleCallback) w.cancelIdleCallback(id);
        else window.clearTimeout(id);
      }
      unsub?.();
    };
  }, []);

  // Sync the signed-in uid to the client-side stores so favorites,
  // watchlist, and watch history switch from localStorage to Firestore
  // (and back) automatically.
  useEffect(() => {
    const uid = user?.id ?? null;
    void Promise.all([
      import("./favorites"),
      import("./watchlist"),
      import("./watch-history"),
    ]).then(([favs, wl, hist]) => {
      favs.setActiveUid(uid);
      wl.setActiveUid(uid);
      hist.setActiveUid(uid);
    }).catch(() => { /* ignore */ });
  }, [user?.id]);

  const signUp: UserAuthState["signUp"] = async ({ email, password, displayName, username }) => {
    const [{ createUserWithEmailAndPassword, sendEmailVerification, signOut: fbSignOut, updateProfile: fbUpdateProfile }, { doc, serverTimestamp, setDoc }, { getDb, getFirebaseAuth }] = await Promise.all([
      import("firebase/auth"),
      import("firebase/firestore"),
      import("./firebase"),
    ]);
    const auth = getFirebaseAuth();
    const db = getDb();
    if (!auth) return { ok: false, error: "Firebase غير متاح" };
    const normalized = email.trim().toLowerCase();
    const dn = (displayName || email.split("@")[0]).trim();

    // Step 1: create the auth account. Only failures here are user-visible
    // ("email already in use", "weak password", etc.).
    let u;
    try {
      const cred = await createUserWithEmailAndPassword(auth, normalized, password);
      u = cred.user;
    } catch (e) {
      const code = (e as { code?: string }).code ?? "";
      return { ok: false, error: fbErr(code) };
    }

    // Step 2: best-effort post-creation work. Any failure here is NON-fatal —
    // the account exists, the user must verify their email next; we should not
    // show a generic "حدث خطأ" after a successful signup.
    try {
      await fbUpdateProfile(u, { displayName: dn });
    } catch {
      /* non-fatal */
    }
    if (db) {
      try {
        await setDoc(doc(db, "users", u.uid), {
          email: u.email,
          username: (username || dn).trim(),
          displayName: dn,
          avatar: null,
          banned: false,
          createdAt: serverTimestamp(),
        });
      } catch {
        /* non-fatal — profile doc can be created on first verified sign-in */
      }
    }
    try {
      await sendEmailVerification(u, { url: window.location.origin + "/login" });
    } catch {
      /* non-fatal — the user can resend later from the success screen */
    }
    try {
      await fbSignOut(auth);
    } catch {
      /* ignore */
    }
    setUser(null);
    setPendingEmail(normalized);
    setNeedsVerification(true);
    const profile: PublicUser = {
      id: u.uid,
      email: u.email ?? "",
      username: (username || dn).trim(),
      displayName: dn,
      emailVerified: false,
      createdAt: Date.now(),
    };
    return { ok: true, value: { ...profile, needsVerification: true } };
  };

  const signIn: UserAuthState["signIn"] = async (email, password) => {
    const [{ sendEmailVerification, signInWithEmailAndPassword, signOut: fbSignOut }, { getFirebaseAuth }] = await Promise.all([
      import("firebase/auth"),
      import("./firebase"),
    ]);
    const auth = getFirebaseAuth();
    if (!auth) return { ok: false, error: "Firebase غير متاح" };
    try {
      const normalized = email.trim().toLowerCase();
      const cred = await signInWithEmailAndPassword(auth, normalized, password);
      const u = cred.user;
      if (!u.emailVerified && !isAdminEmail(u.email)) {
        try {
          await sendEmailVerification(u, { url: window.location.origin + "/login" });
        } catch {
          /* non-fatal */
        }
        try {
          await fbSignOut(auth);
        } catch {
          /* ignore */
        }
        setPendingEmail(normalized);
        setNeedsVerification(true);
        return {
          ok: false,
          error: "يجب تأكيد البريد الإلكتروني أولاً. تم إرسال رسالة جديدة إلى بريدك.",
        };
      }
      const base: PublicUser = {
        id: u.uid,
        email: u.email ?? "",
        username: (u.displayName || u.email?.split("@")[0] || "user").trim(),
        displayName: (u.displayName || u.email?.split("@")[0] || "User").trim(),
        avatar: u.photoURL ?? undefined,
        emailVerified: u.emailVerified,
        createdAt: Date.now(),
      };
      const profile = await loadProfile(u.uid, base);
      if (profile.banned) {
        await fbSignOut(auth);
        return { ok: false, error: "تم حظر هذا الحساب من قِبَل الإدارة" };
      }
      setUser({ ...profile, emailVerified: true });
      setNeedsVerification(false);
      setPendingEmail(null);
      return { ok: true, value: profile };
    } catch (e) {
      const code = (e as { code?: string }).code ?? "";
      return { ok: false, error: fbErr(code) };
    }
  };

  const resendVerification: UserAuthState["resendVerification"] = async () => {
    const [{ sendEmailVerification }, { getFirebaseAuth }] = await Promise.all([
      import("firebase/auth"),
      import("./firebase"),
    ]);
    const auth = getFirebaseAuth();
    if (!auth || !auth.currentUser) {
      return { ok: false, error: "أعد محاولة تسجيل الدخول لإرسال الرسالة" };
    }
    try {
      await sendEmailVerification(auth.currentUser, {
        url: window.location.origin + "/login",
      });
      return { ok: true };
    } catch {
      return { ok: false, error: "تعذّر إرسال الرسالة، حاول لاحقاً" };
    }
  };

  const signOut = async () => {
    const [{ signOut: fbSignOut }, { getFirebaseAuth }] = await Promise.all([
      import("firebase/auth"),
      import("./firebase"),
    ]);
    const auth = getFirebaseAuth();
    if (auth) {
      try {
        await fbSignOut(auth);
      } catch {
        /* ignore */
      }
    }
    setUser(null);
    setNeedsVerification(false);
    setPendingEmail(null);
  };

  const updateProfile: UserAuthState["updateProfile"] = async (patch) => {
    const [{ updateProfile: fbUpdateProfile }, { doc, updateDoc }, { getDb, getFirebaseAuth }] = await Promise.all([
      import("firebase/auth"),
      import("firebase/firestore"),
      import("./firebase"),
    ]);
    const auth = getFirebaseAuth();
    const db = getDb();
    if (!user || !auth || !db) return { ok: false, error: "غير مسجل" };
    try {
      if (patch.displayName && auth.currentUser) {
        try {
          await fbUpdateProfile(auth.currentUser, { displayName: patch.displayName });
        } catch {
          /* non-fatal */
        }
      }
      if (patch.avatar !== undefined && auth.currentUser) {
        try {
          await fbUpdateProfile(auth.currentUser, { photoURL: patch.avatar ?? "" });
        } catch {
          /* non-fatal */
        }
      }
      const clean: Record<string, unknown> = {};
      if (patch.displayName !== undefined) clean.displayName = patch.displayName;
      if (patch.username !== undefined) clean.username = patch.username;
      if (patch.avatar !== undefined) clean.avatar = patch.avatar;
      await updateDoc(doc(db, "users", user.id), clean);
      const next = { ...user, ...patch };
      setUser(next);
      const { primeUserProfile } = await import("./user-profiles");
      primeUserProfile(user.id, {
        displayName: next.displayName ?? null,
        avatar: next.avatar ?? null,
      });
      return { ok: true };
    } catch {
      return { ok: false, error: "تعذّر حفظ التعديلات" };
    }
  };

  const isAdmin = !!user && isAdminEmail(user.email);

  return (
    <Ctx.Provider
      value={{
        user,
        loading,
        isAdmin,
        needsVerification,
        pendingEmail,
        resendVerification,
        signUp,
        signIn,
        signOut,
        updateProfile,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useUserAuth() {
  const c = useContext(Ctx);
  if (!c) throw new Error("useUserAuth must be used inside UserAuthProvider");
  return c;
}
